import { Flex } from 'app/components/common/Flex';
import { notification, Radio, Select, Space } from 'antd';
import { LoadingOutlined, ReloadOutlined, SettingFilled } from '@ant-design/icons';
import React, { PureComponent } from 'react';
import TimeUtils from 'app/common/util/TimeUtils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import {
    CapacityModel,
    CapacityValue,
    CountsAlgorithm,
    CountType,
    FlightId,
    HotspotDto,
    MonitoredLoadValue,
    MonitoredTv,
    Otmv,
    OtmvDurationsRequestDto,
    StandardCountSubtotalsType,
    TimePeriod,
    TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_MONITOR_REQUEST_CLAZZ_VALUE,
    TrafficCountsByTvRequestDto,
    TrafficCountsDto,
    TrafficMonitorRequest,
    TrafficType,
} from 'app/model/CORE';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { CountsGraph } from '../graph/CountsGraph';
import TrafficCountUtils from 'app/common/util/TrafficCountUtils';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import AppContext from 'app/common/context/AppContext';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import memoizeOne from 'memoize-one';
import { RegulationPeriods } from 'app/model/client/CORE';
import CapacityUtils from 'app/common/util/CapacityUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import { ConvectionSource, TvMeteoAlert } from 'app/model/SOL49';
import { MeteoLoadBar } from 'app/components/dashboard/network/flows/svg/MeteoLoadBar';
import { HotspotBar } from 'app/components/dashboard/network/flows/svg/HotspotBar';

const DEFAULT_OTMV_DURATION = 1;

const getInitialState = (countType: CountType, predictedCapacity: CapacityValue[]): TvDetailsState => ({
    display: countType,
    entryCounts: null,
    occCounts: null,
    monitoredTv: null,
    meteoAlerts: [],
    predictedCapacity: predictedCapacity,
    timeOver: null,
    entryInterval: 20,
    entryStep: 60,
    entryTrafficType: TrafficType.LOAD,
    otmvDurations: null,
    otmvDuration: DEFAULT_OTMV_DURATION,
    occTrafficType: TrafficType.LOAD,
    loading: false,
});

interface TvDetailsProps {
    currentTime: Date;
    timePeriod: TimePeriod;
    tvId: string;
    tvSet: string;
    regulationPeriods?: Array<RegulationPeriods>;
    proposalRegulationPeriods?: Array<RegulationPeriods>;
    occupancyPeriods?: Map<FlightId, TimePeriod>;
    onUpdate?(): void;
    selectedPeriod?: TimePeriod;
    selectedPeriodType?: CountType;
    onSelectedPeriodChange?: (
        countType: CountType,
        selectedPeriod: TimePeriod,
        trafficWindow: TimePeriod,
        hourlyEntryCapacity?: Array<CapacityValue>,
        otmvs?: Array<Otmv>,
    ) => void;
    onTrafficTypeChange?: (trafficType: TrafficType) => void;
    onCountTypeChange?: (countType: CountType) => void;
    onOtmvDurationChange?: (otmvDuration: number) => void;
    onManualRefresh?: () => void;
    hotspot?: HotspotDto;
    predictedCapacity?: CapacityValue[];
}

interface TvDetailsState {
    display: CountType;

    // Data
    entryCounts: TrafficCountsDto;
    occCounts: TrafficCountsDto;
    monitoredTv: MonitoredTv;
    meteoAlerts: TvMeteoAlert[];
    predictedCapacity: CapacityValue[];

    // Internal state
    timeOver: Date;
    entryInterval: number;
    entryStep: number;
    entryTrafficType: TrafficType;
    otmvDurations: number[]; // Durations for which we have OTMV Values
    otmvDuration: number;
    occTrafficType: TrafficType;

    loading: boolean;
}

export class TvDetails extends PureComponent<TvDetailsProps, TvDetailsState> {
    state = getInitialState(this.props.selectedPeriodType || CountType.ENTRY, this.props.predictedCapacity);
    componentDidMount(): void {
        this.refresh(true);
    }
    componentDidUpdate(prevProps) {
        if (this.props?.tvId !== prevProps?.tvId) {
            this.setState(
                getInitialState(this.props.selectedPeriodType || CountType.ENTRY, this.props.predictedCapacity),
                () => this.refresh(true),
            );
        }
    }
    refreshOtmvDurations = () => {
        const req: OtmvDurationsRequestDto = {
            dataset: AppContext.getDataset(),
            day: AppContext.getMonitoredDay(),
            tvId: this.props.tvId,
        };
        this.setState({ loading: true });
        ServiceFactory.coreService()
            .getOtmvDurations(req)
            .then(reply => {
                this.refresh(false, reply.data?.durations);
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    refreshEntryCounts = () => {
        this.setState({ loading: true });
        const req: TrafficCountsByTvRequestDto = {
            clazz: TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            trafficTypes: [TrafficType.LOAD, TrafficType.DEMAND, TrafficType.REGULATED_DEMAND],
            trafficWindow: this.props.timePeriod,
            algorithm: CountsAlgorithm.PSEUDO_NM,
            countType: CountType.ENTRY,
            durationInMin: this.state.entryStep,
            intervalInMin: this.state.entryInterval,
            includeForecastFlights: false,
            subtotalsType: StandardCountSubtotalsType.NM,
            tvId: this.props.tvId,
            computeHourlyEntryRates: true,
            computeHotspots: false,
        };
        ServiceFactory.coreService()
            .getTrafficCounts(req)
            .then(reply => {
                this.setState({ entryCounts: reply.data });
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    refreshOccupancyCounts = () => {
        this.setState({ loading: true });
        const req: TrafficCountsByTvRequestDto = {
            clazz: TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            trafficTypes: [TrafficType.LOAD, TrafficType.DEMAND, TrafficType.REGULATED_DEMAND],
            trafficWindow: this.props.timePeriod,
            algorithm: CountsAlgorithm.PSEUDO_NM,
            countType: CountType.OCCUPANCY,
            durationInMin: this.state.otmvDuration,
            intervalInMin: 1,
            includeForecastFlights: false,
            subtotalsType: StandardCountSubtotalsType.NM,
            tvId: this.props.tvId,
            computeOtmvDefns: true,
        };
        ServiceFactory.coreService()
            .getTrafficCounts(req)
            .then(reply => {
                this.setState({ occCounts: reply.data });
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    refreshTrafficMonitorData = () => {
        this.setState({ loading: true });
        const req: TrafficMonitorRequest = {
            clazz: TRAFFIC_MONITOR_REQUEST_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            adhocTvList: [this.props.tvId],
            capacityModel: AppContext.getCapacityModel(),
            entryCountsAlgorithm: CountsAlgorithm.PSEUDO_NM,
            occupancyCountsAlgorithm: CountsAlgorithm.PSEUDO_NM,
            timePeriod: this.props.timePeriod,
            includeForecastFlights: false,
        };
        ServiceFactory.coreService()
            .getTrafficMonitorData(req)
            .then(reply => {
                this.setState({ monitoredTv: reply.data?.monitoredTvSets[0].monitoredTvs[0] });
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    refresh = (refreshOtmvDurations: boolean = false, otmvDurations?: number[]) => {
        // Don't make server requests if required info has not been provided
        if (!this.props.tvId || !this.props.timePeriod) return false;

        if (refreshOtmvDurations) {
            this.refreshOtmvDurations(); // Will call refresh(false) when done
        } else {
            this.props.onUpdate?.();

            // TODO: setState at the same time as the data refresh, to prevent from having 2 renderings
            if (otmvDurations) {
                const regOtmvDuration =
                    this.props.regulationPeriods?.find(regPeriod => regPeriod.occupancyDuration)?.occupancyDuration /
                    TimeUtils.MIN_TO_MS;
                const otmvDuration = regOtmvDuration || otmvDurations?.[0] || DEFAULT_OTMV_DURATION;
                this.setState({ otmvDurations: otmvDurations, otmvDuration: otmvDuration }, () => {
                    this.props.onOtmvDurationChange?.(otmvDuration);
                    this.refresh();
                });
            } else {
                // TODO: Regroup those 3 queries into one?
                this.refreshEntryCounts();
                this.refreshOccupancyCounts();
                this.refreshTrafficMonitorData();
                this.getMeteoAlerts();
            }
        }
    };

    getMeteoAlerts = () => {
        ServiceFactory.sol49Service()
            .getTvMeteoAlerts(this.props.tvId, AppContext.getConvectionSource())
            .then(reply => {
                if (reply.error) {
                    console.error('Error fetching meteo alerts for tv:', this.props.tvId, ' => ', reply.error);
                } else {
                    this.setState(
                        {
                            meteoAlerts: reply.data,
                        },
                        () =>
                            Utils.isNotEmpty(this.state.meteoAlerts) &&
                            Utils.isNotExists(this.state.predictedCapacity) &&
                            this.getPredictedCapacity(),
                    );
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };

    getPredictedCapacity = () => {
        if (Utils.isEmpty(this.state.meteoAlerts)) return;

        // ISOBAR service is DWD specific (needs DWD convectionFraction and Likeliness info)
        if (
            AppContext.getConvectionSource() === ConvectionSource.DWD ||
            AppContext.getConvectionSource() === ConvectionSource.DWD_ICONV
        ) {
            const obj = {};
            obj[this.props.tvId] = this.state.meteoAlerts;
            ServiceFactory.sol49Service()
                .getPredictedCapacity(AppContext.getConvectionSource(), obj)
                .then(reply => {
                    const error =
                        reply.error ||
                        (Utils.isNotEmpty(reply.data.errors) &&
                            reply.data.errors.find(
                                e => e.message.includes('No inference') || e.details?.includes('No inference'),
                            ));
                    if (error) {
                        console.error('Error fetching predicted capacity for tv:', this.props.tvId, ' => ', error);
                        notification.error({ message: error.message, description: error?.details });
                    } else {
                        this.setState({ predictedCapacity: reply.data.predictions[0].predictedCapacity });
                    }
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        }
    };

    handleTimeOverChange = (timeOver: Date) => {
        this.setState({ timeOver: timeOver });
    };
    handleSelectedPeriodChange = (countType: CountType) => (selectedPeriod: TimePeriod) => {
        let trafficWindow;
        if (TimePeriodUtils.isValid(selectedPeriod)) {
            if (countType === CountType.ENTRY) {
                trafficWindow = TrafficCountUtils.countingPeriodFor2(
                    selectedPeriod,
                    this.state.entryInterval,
                    this.state.entryStep,
                );
            } else {
                trafficWindow = TrafficCountUtils.countingPeriodFor2(selectedPeriod, 1, this.state.otmvDuration);
            }
        }

        this.props.onSelectedPeriodChange?.(
            countType,
            selectedPeriod,
            trafficWindow,
            this.state.entryCounts?.hourlyEntryCapacity,
            this.state.occCounts?.otmvDefns,
        );
    };
    handleEntryTrafficTypeChange = (trafficType: TrafficType) => {
        this.setState({ entryTrafficType: trafficType });
        this.handleTrafficTypeChange(trafficType);
    };
    handleOccTrafficTypeChange = (trafficType: TrafficType) => {
        this.setState({ occTrafficType: trafficType });
        this.handleTrafficTypeChange(trafficType);
    };
    handleTrafficTypeChange(trafficType: TrafficType) {
        if (trafficType) this.props.onTrafficTypeChange(trafficType);
    }
    handleRefreshClick = () => {
        if (this.props.onManualRefresh) {
            this.props.onManualRefresh();
        }
        this.refresh();
    };

    setEntryInterval = (entryInterval: number) => {
        this.setState({ entryInterval: entryInterval }, () => this.refresh());
    };
    setEntryStep = (entryDuration: number) => {
        this.setState({ entryStep: entryDuration }, () => this.refresh());
    };
    setOtmvDuration = (duration: number) => {
        this.setState({ otmvDuration: duration }, () => {
            this.props.onOtmvDurationChange?.(duration);
            this.refresh();
        });
    };
    getActivePeriods = memoizeOne((monitoredLoadValues: Array<MonitoredLoadValue>) => {
        return monitoredLoadValues && MonitoredLoadUtils.getActivePeriods(monitoredLoadValues);
    });

    renderEntryCounts = () => {
        const hourlyEntryCapacity =
            AppContext.getCapacityModel() === CapacityModel.WCM_NRP
                ? CapacityUtils.getWcmCapacity(
                      this.state.entryCounts?.hourlyEntryCapacity,
                      this.state.entryCounts?.timeRange,
                  )
                : this.state.entryCounts?.hourlyEntryCapacity;

        const selectedPeriod =
            this.props.selectedPeriod &&
            (this.props.selectedPeriodType === CountType.ENTRY
                ? this.props.selectedPeriod
                : TrafficCountUtils.alignedTimePeriod(this.props.selectedPeriod, this.state.entryInterval));
        return (
            <CountsGraph
                currentTime={this.props.currentTime}
                counts={
                    this.state.entryCounts &&
                    TrafficCountUtils.getCountsFor(this.state.entryCounts, this.state.entryTrafficType)
                }
                activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                timeRange={this.state.entryCounts?.timeRange}
                hourlyEntryCapacity={hourlyEntryCapacity}
                predictedCapacity={this.state.predictedCapacity}
                regulationPeriods={this.props.regulationPeriods}
                proposalRegulationPeriods={this.props.proposalRegulationPeriods}
                highlightedFlightsType={CountType.ENTRY}
                highlightedFlightsOccupancy={this.props.occupancyPeriods}
                timeOver={this.state.timeOver}
                onTimeOverChange={this.handleTimeOverChange}
                selectedPeriod={selectedPeriod}
                onSelectedPeriodChange={this.handleSelectedPeriodChange(CountType.ENTRY)}
            />
        );
    };
    renderEntryLoad = () => {
        const monitoredPeriod = this.state.entryCounts?.timeRange;
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    E
                </Flex>
                <Flex direction="vertical">
                    <EntryLoadBar
                        loadValues={this.state.monitoredTv?.loadValues}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                    <MeteoLoadBar
                        loadValues={MonitoredLoadUtils.getTvMeteoAlertsLoadValues(
                            this.state.meteoAlerts,
                            monitoredPeriod,
                        )}
                        monitoredPeriod={monitoredPeriod}
                        barHeightEm={0.5}
                        hideLabel={true}
                    />
                    <HotspotBar barHeight={1} hotspot={this.props.hotspot} monitoredPeriod={monitoredPeriod} />
                </Flex>
            </Flex>
        );
    };
    renderEntry = () => {
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="20em">{this.renderEntryCounts()}</Flex>
                    <Flex size="1em">{this.renderEntryLoad()}</Flex>
                </Flex>
                <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                    <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5em' }}>
                        <ConditionalRender condition={this.state.loading}>
                            <LoadingOutlined style={{ fontSize: '2em' }} />
                        </ConditionalRender>
                        <ConditionalRender condition={!this.state.loading}>
                            <ReloadOutlined
                                title="Refresh"
                                onClick={this.handleRefreshClick}
                                style={{ fontSize: '2em', cursor: 'pointer' }}
                            />
                        </ConditionalRender>
                        <SettingFilled style={{ fontSize: '2em' }} />
                    </div>
                    <Space direction="vertical">
                        <Select value="by_type" style={{ width: '100%' }}>
                            <Select.Option value="by_type">By type</Select.Option>
                            <Select.Option disabled={true} value="by_flow">
                                By flow
                            </Select.Option>
                        </Select>
                        <Select
                            value={this.state.entryTrafficType}
                            onChange={this.handleEntryTrafficTypeChange}
                            style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                            <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                            <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                        </Select>
                        <Space direction="horizontal">
                            <Flex direction={'vertical'}>
                                <span>Interval</span>
                                <Select
                                    value={this.state.entryInterval}
                                    onChange={value => this.setEntryInterval(value)}
                                    style={{ width: '100%' }}>
                                    <Select.Option value={5}>5</Select.Option>
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={60}>60</Select.Option>
                                </Select>
                            </Flex>
                            <Flex direction={'vertical'}>
                                <span>Step</span>
                                <Select
                                    value={this.state.entryStep}
                                    onChange={value => this.setEntryStep(value)}
                                    style={{ width: '100%' }}>
                                    <Select.Option value={5}>5</Select.Option>
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={60}>60</Select.Option>
                                </Select>
                            </Flex>
                        </Space>
                    </Space>
                </Flex>
            </Flex>
        );
    };
    renderOccupancyCounts = () => {
        const selectedPeriod =
            this.props.selectedPeriod &&
            (this.props.selectedPeriodType === CountType.OCCUPANCY
                ? this.props.selectedPeriod
                : TrafficCountUtils.alignedTimePeriod(this.props.selectedPeriod, 1));
        return (
            <CountsGraph
                currentTime={this.props.currentTime}
                counts={
                    this.state.occCounts &&
                    TrafficCountUtils.getCountsFor(this.state.occCounts, this.state.occTrafficType)
                }
                activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                timeRange={this.state.occCounts?.timeRange}
                otmvDefns={this.state.occCounts?.otmvDefns}
                regulationPeriods={this.props.regulationPeriods}
                proposalRegulationPeriods={this.props.proposalRegulationPeriods}
                highlightedFlightsType={CountType.OCCUPANCY}
                highlightedFlightsOccupancy={this.props.occupancyPeriods}
                timeOver={this.state.timeOver}
                onTimeOverChange={this.handleTimeOverChange}
                selectedPeriod={selectedPeriod}
                onSelectedPeriodChange={this.handleSelectedPeriodChange(CountType.OCCUPANCY)}
            />
        );
    };
    renderOccupancyLoad = () => {
        const occLoad =
            (this.state.monitoredTv?.occupancyLoadValues?.length > 0 &&
                this.state.monitoredTv?.occupancyLoadValues[0].occupancyLoadValues) ||
            [];
        const monitoredPeriod = this.state.occCounts?.timeRange;
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    O
                </Flex>
                <Flex direction="vertical">
                    <OccupancyLoadBar
                        loadValues={occLoad}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                    <MeteoLoadBar
                        loadValues={MonitoredLoadUtils.getTvMeteoAlertsLoadValues(
                            this.state.meteoAlerts,
                            monitoredPeriod,
                        )}
                        monitoredPeriod={monitoredPeriod}
                        barHeightEm={0.5}
                        hideLabel={true}
                    />
                    <HotspotBar barHeight={1} hotspot={this.props.hotspot} monitoredPeriod={monitoredPeriod} />
                </Flex>
            </Flex>
        );
    };
    /**
     * Occupancy is not displayed until data could be retrieved.
     * If TvId refers to a refloc != AS, then there won't be occupancy counts returned
     */
    renderOccupancy = () => {
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="20em">{this.renderOccupancyCounts()}</Flex>
                    <Flex size="1em">{this.renderOccupancyLoad()}</Flex>
                </Flex>
                <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                    <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5em' }}>
                        <ConditionalRender condition={this.state.loading}>
                            <LoadingOutlined style={{ fontSize: '2em' }} />
                        </ConditionalRender>
                        <ConditionalRender condition={!this.state.loading}>
                            <ReloadOutlined
                                title="Refresh"
                                onClick={this.handleRefreshClick}
                                style={{ fontSize: '2em', cursor: 'pointer' }}
                            />
                        </ConditionalRender>
                        <SettingFilled style={{ fontSize: '2em' }} />
                    </div>
                    <Space direction="vertical">
                        <Select value="by_type" style={{ width: '100%' }}>
                            <Select.Option value="by_type">by type</Select.Option>
                        </Select>
                        <Select
                            value={this.state.occTrafficType}
                            onChange={this.handleOccTrafficTypeChange}
                            style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                            <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                            <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                        </Select>
                        <Select
                            value={this.state.otmvDuration}
                            onChange={value => this.setOtmvDuration(value)}
                            style={{ width: '100%' }}>
                            {Array.from(Array(20).keys()).map(duration => {
                                return (
                                    <Select.Option key={`duration-${duration + 1}`} value={duration + 1}>
                                        {duration + 1} min {this.state.otmvDurations?.includes(duration + 1) ? '*' : ''}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Space>
                </Flex>
            </Flex>
        );
    };
    render() {
        return (
            <Flex direction="vertical">
                <div style={{ paddingLeft: '3em' }}>
                    <Radio.Group
                        value={this.state.display}
                        onChange={value =>
                            this.setState({ display: value.target.value }, () =>
                                this.props.onCountTypeChange?.(value.target.value),
                            )
                        }>
                        <Radio.Button
                            style={{ width: '8em', textAlign: 'center' }}
                            disabled={!Utils.isExists(this.state.entryCounts)}
                            value={CountType.ENTRY}>
                            Entry
                        </Radio.Button>
                        <Radio.Button
                            style={{ width: '8em', textAlign: 'center' }}
                            disabled={!Utils.isExists(this.state.occCounts)}
                            value={CountType.OCCUPANCY}>
                            Occupancy
                        </Radio.Button>
                    </Radio.Group>
                </div>
                {this.state.display === CountType.ENTRY && this.renderEntry()}
                {this.state.display === CountType.OCCUPANCY && this.renderOccupancy()}
            </Flex>
        );
    }
}
